import { CSSResultGroup, html, LitElement } from 'lit'
import { customElement, property, query, queryAssignedElements, state } from 'lit/decorators.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { style } from './marquee.style.js'

@customElement('csc-marquee')
export class CSCMarquee extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Boolean, attribute: 'pause-on-hover' })
  pauseOnHover: boolean = false

  @property({ type: Boolean })
  play: boolean = false

  @property({ type: Number })
  speed: number = 50

  @property({ type: Number })
  gap: number = 30

  @property({ type: Number, attribute: 'gradient-width' })
  gradientWidth: number = 0

  @state()
  containerWidth: number = 0

  @state()
  marqueeWidth: number = 0

  @state()
  multiplier: number = 1 

  @state()
  duration: number = 0

  @queryAssignedElements({ flatten: true })
  _content!: Array<HTMLElement>

  @query('.marquee-container')
  marqueeContainer!: HTMLDivElement

  @query('.marquee-initial')
  marqueeInitial!: HTMLDivElement

  @query('#init')
  initContainer!: HTMLDivElement

  @query('#cloned')
  cloneContainer!: HTMLDivElement

  
  protected override updated() {
    this.setGap()

    requestAnimationFrame(() => {
      this.calculate()
      this.renderChildren()
    })
  }

  private setGap() : void {
    if (this._content.length === 0) { return }

    this._content.forEach((element) => {
      element.setAttribute('style', `margin-right: ${this.gap}px`)
    })
  }

  private calculate() {
    const containerRect = this.marqueeContainer.getBoundingClientRect()
    const marqueeRect = this.marqueeInitial.getBoundingClientRect()
    const containerWidth = containerRect.width
    const marqueeWidth = marqueeRect.width

    this.multiplier = marqueeWidth < containerWidth 
      ? Math.ceil(containerWidth / marqueeWidth) 
      : 1

    this.containerWidth = containerWidth
    this.marqueeWidth = marqueeWidth
    this.duration= (marqueeWidth * this.multiplier) / this.speed
  }

  private renderChildren() : void {
    this.initContainer.innerHTML = ''
    this.cloneContainer.innerHTML = ''

    if (this._content.length === 0) { return }

    for(let i = 0; i < this.multiplier; i++) {
      this._content.forEach((element) => {
        if (!(i === this.multiplier - 1)) {
          this.initContainer.appendChild(element.cloneNode(true))
        }
        this.cloneContainer.appendChild(element.cloneNode(true))
      })
    }
  }

  override render() {
    return html`
      <div 
        class="marquee-container" 
        style="--internal-marquee-pause-on-hover: ${!this.play || this.pauseOnHover ? 'paused' : 'running'}; --internal-marquee-mask-width: ${this.gradientWidth}px"
      >
        <div class="marquee" style="--internal-marquee-play: ${this.play ? 'running' : 'paused'}; --internal-marquee-duration: ${this.duration}s;">
          <div class="marquee-initial">
            <slot></slot>
          </div>
          <div id="init" class="marquee-clone"></div>
        </div>
        <div id="cloned" class="marquee" style="--internal-marquee-play: ${this.play ? 'running' : 'paused'}; --internal-marquee-duration: ${this.duration}s;">
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-marquee': CSCMarquee,
  }
}
